import { memo, useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  checkDisabled,
  createReservationMode,
  deleteReservationMode,
  discardChanges,
  reservationModesMapSelector,
  selectedFirstDrawerSelector,
  selectedSecondDrawerSelector,
  setFirstDrawer,
  setSecondDrawer,
  updateDrawer,
  updateReservationMode,
} from 'slices/reservationMode.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { DrawerCallback } from 'types/utils.type';
import type { TReservationMode } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import Content from './sections';

interface Props {
  confirmDiscardChanges: (isFirstDrawer: boolean, actionFn: () => void) => void;
}

const ReservationModesDrawer = ({ confirmDiscardChanges }: Props) => {
  const dispatch = useAppDispatch();

  const firstDrawer = useAppSelector(selectedFirstDrawerSelector);
  const secondDrawer = useAppSelector(selectedSecondDrawerSelector);
  const reservationModeMap = useAppSelector(reservationModesMapSelector);

  const setFirstResource = useCallback(
    (reservationMode: TReservationMode) =>
      dispatch(setFirstDrawer(reservationMode)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (reservationMode: TReservationMode) =>
      dispatch(setSecondDrawer(reservationMode)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    reservationModeMap,
    firstDrawer,
    secondDrawer,
    setFirstResource,
    setSecondResource
  );

  const firstDrawerDisabled = useMemo(
    () => dispatch(checkDisabled(true)),
    [dispatch, firstDrawer]
  );

  const secondDrawerDisabled = useMemo(
    () => dispatch(checkDisabled(false)),
    [dispatch, secondDrawer]
  );

  const onDelete = useCallback(
    (isFirstDrawer: boolean, { id }: TReservationMode) => {
      dispatch(deleteReservationMode({ reservationModeId: id, isFirstDrawer }));
    },
    [dispatch]
  );

  const onSave = useCallback(
    (isFirstDrawer: boolean, reservationMode: TReservationMode) => {
      if (reservationMode.id === 0) {
        dispatch(createReservationMode(reservationMode));
      } else {
        dispatch(updateReservationMode({ isFirstDrawer, reservationMode }));
      }
    },
    [dispatch]
  );

  const onClose = useCallback(
    (isFirstDrawer: boolean) => {
      confirmDiscardChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstDrawer(undefined));
        } else {
          dispatch(setSecondDrawer(undefined));
        }
      });
    },
    [confirmDiscardChanges, dispatch]
  );

  const onChange = useCallback<DrawerCallback<TReservationMode>>(
    (isFirstDrawer, key, value) =>
      dispatch(updateDrawer({ isFirstDrawer, key, value })),
    [dispatch]
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstDrawer?.name || '',
        content: (
          <Content
            data={firstDrawer}
            onChange={(key, value) => onChange(true, key, value)}
          />
        ),
        drawerOpen: !!firstDrawer,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('reservation_mode_confirmation_delete')}
            handleDelete={() => onDelete(true, firstDrawer!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstDrawer!.id,
                })
              )
            }
            handleSave={() => onSave(true, firstDrawer!)}
            isSaveDisable={firstDrawerDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondDrawer?.name || '',
        content: (
          <Content
            data={secondDrawer}
            onChange={(key, value) => onChange(false, key, value)}
          />
        ),
        drawerOpen: !!secondDrawer,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('reservation_mode_confirmation_delete')}
            handleDelete={() => onDelete(false, secondDrawer!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondDrawer!.id,
                })
              )
            }
            handleSave={() => onSave(false, secondDrawer!)}
            isSaveDisable={secondDrawerDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default memo(ReservationModesDrawer);
